import React, { useEffect, useState, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useMessages } from "context/messages";
import { getToken } from "context/auth";

import Widget from "components/dashboard/widget";
import WidgetLoadingSkeleton from "components/dashboard/widgetLoadingSkeleton";
import ButtonText from "components/common/buttonText";
import ButtonSlider from "components/common/buttonSlider";
import InputSearch from "components/common/inputSearch";
import Modal from "components/common/modal";
import InputPhoneNumber from "components/common/inputPhoneNumber";
import Paging from "components/common/paging";
import Tabs from "components/common/tabs";
import Stepper from "components/common/stepper";

import { getContractCodes, uploadContractCodes, previewContractCodes, resetContractCode, revokeContractCode, issuePermitWithCode, reactivateContractCode, deleteContractCode, getAvailablePermitsForIssue, getAvailablePermitsForIssuePaged, scheduleTransition, cancelTransition, getVehiclesForPhone, createVehicleForPhone } from "api/contractCodes";
import { searchUserByPhoneNumber } from "api/users";

const ContractCodesPage = () => {
  const { t } = useTranslation();
  const { showToast } = useMessages();
  const { parkingAreaExtId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, used: 0, available: 0, inactive: 0, availablePermits: 0, scheduledTransitions: 0 });
  const [searchText, setSearchText] = useState("");
  const [filteredCodes, setFilteredCodes] = useState([]);
  const [csvText, setCsvText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedCodeForIssue, setSelectedCodeForIssue] = useState(null);
  const [issuePhoneNumber, setIssuePhoneNumber] = useState("");
  const [issuePhoneCountryCode, setIssuePhoneCountryCode] = useState("");
  const [issueRegPlate, setIssueRegPlate] = useState("");
  const [availablePermitsList, setAvailablePermitsList] = useState([]);
  const [selectedPermitExtId, setSelectedPermitExtId] = useState("");
  // Anton-review C3: paginering/sök för platslista vid utfärdande (400+ platser)
  const [permitSearchQuery, setPermitSearchQuery] = useState("");
  const [permitTotalCount, setPermitTotalCount] = useState(0);
  const [permitsLoading, setPermitsLoading] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCodeForSchedule, setSelectedCodeForSchedule] = useState(null);
  const [schedulePhoneNumber, setSchedulePhoneNumber] = useState("");
  const [schedulePhoneCountryCode, setSchedulePhoneCountryCode] = useState("");
  const [scheduleActiveDate, setScheduleActiveDate] = useState("");
  const [isScheduling, setIsScheduling] = useState(false);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [scheduleVehicles, setScheduleVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newRegPlate, setNewRegPlate] = useState("");
  const [isCreatingVehicle, setIsCreatingVehicle] = useState(false);
  const [schedulePhoneLookupDone, setSchedulePhoneLookupDone] = useState(false);
  const [issueUserNotRegistered, setIssueUserNotRegistered] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Status-filter på Översikt-fliken: 'all' | 'available' | 'used' | 'scheduled' | 'inactive'.
  // Driver klickbara stats-kort som filtrerar koder-tabellen nedanför.
  const [statusFilter, setStatusFilter] = useState('all');
  // 2026-04-30 Anton-review backfill: backend-driven per-rad preview
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const fileInputRef = useRef(null);

  // Column mapping (used when a file with multiple columns is loaded)
  const [rawRows, setRawRows] = useState(null);
  const [hasHeader, setHasHeader] = useState(false);
  const [codeColumnIndex, setCodeColumnIndex] = useState(0);
  const [phoneColumnIndex, setPhoneColumnIndex] = useState(null);
  const [regPlateColumnIndex, setRegPlateColumnIndex] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);

  // Tab-state — uppdelning av sidan i Översikt / Ladda upp / Schemalagda byten
  const [activeTab, setActiveTab] = useState('overview');

  // Upload-stepper: 0 Förstå format, 1 Välj fil, 2 Mappa kolumner, 3 Förhandsgranska, 4 Klart.
  // För manuell inmatning (csvText utan rawRows) hoppas Mappa kolumner-steget över via canAdvance.
  const [uploadStep, setUploadStep] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [parkingAreaExtId]);

  useEffect(() => {
    if (codes) {
      let filtered = [...codes];

      // Status-filter — drivs av klickbara stats-kort på Översikt
      if (statusFilter === 'available') filtered = filtered.filter(c => c.isActive && !c.isUsed);
      else if (statusFilter === 'used') filtered = filtered.filter(c => c.isActive && c.isUsed);
      else if (statusFilter === 'scheduled') filtered = filtered.filter(c => c.hasQueuedTransition);
      else if (statusFilter === 'inactive') filtered = filtered.filter(c => !c.isActive);

      if (searchText) {
        const search = searchText.toLowerCase();
        filtered = filtered.filter(code =>
          code.code?.toLowerCase().includes(search) ||
          code.usedByUserPhone?.toLowerCase().includes(search) ||
          code.usedByUserName?.toLowerCase().includes(search)
        );
      }
      setFilteredCodes(filtered);
      setTotalPages(Math.ceil(filtered.length / 10));
      setCurrentPage(0);
    }
  }, [codes, searchText, statusFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      // Hämta ALLA koder (inkl inaktiva) — filtrering sker klient-side via statusFilter
      const codesResult = await getContractCodes(token, parkingAreaExtId, true);

      if (codesResult && !codesResult.error) {
        setCodes(codesResult.codes || []);
        setStatistics({
          total: codesResult.totalCodes || 0,
          used: codesResult.usedCodes || 0,
          available: codesResult.availableCodes || 0,
          inactive: codesResult.inactiveCodes || codesResult.InactiveCodes || 0,
          availablePermits: codesResult.availablePermitsWithContractCodeRequirement || 0,
          scheduledTransitions: codesResult.scheduledTransitions || 0
        });
      }
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Normalize phone number: strip leading zero and add country code if needed
  const normalizePhone = (raw) => {
    if (!raw) return null;
    let phone = String(raw).trim().replace(/[\s\-().]/g, '');
    if (!phone) return null;
    if (phone.startsWith('+')) return phone;
    if (phone.startsWith('00')) return '+' + phone.substring(2);
    if (phone.startsWith('0')) return '+46' + phone.substring(1);
    // Bare digits without country code — assume +46
    if (/^[0-9]{7,12}$/.test(phone)) return '+46' + phone;
    return null;
  };

  const looksLikePhone = (value) => {
    if (!value) return false;
    const cleaned = String(value).trim().replace(/[\s\-().]/g, '');
    return /^(\+|00)?[0-9]{7,15}$/.test(cleaned);
  };

  // Returns true if a value looks like a column header (text label, not an actual code)
  const looksLikeHeader = (value) => {
    if (!value) return false;
    const lower = value.toLowerCase().trim();
    const knownHeaders = ['kod', 'code', 'kontraktsnummer', 'contract', 'contractcode', 'kontraktskod',
      'nummer', 'number',
      'telefon', 'phone', 'mobilnummer', 'mobile', 'mobil', 'tel', 'nr',
      'reg', 'regnr', 'reg.nr', 'registreringsnummer', 'registration', 'plate', 'license', 'licenseplate', 'fordon', 'vehicle'];
    return knownHeaders.includes(lower);
  };

  const looksLikeRegPlate = (value) => {
    if (!value) return false;
    const cleaned = String(value).trim().toUpperCase();
    return /^[A-Z]{2,3}\s?[0-9]{2,3}[A-Z0-9]{0,3}$/.test(cleaned);
  };

  // Parse raw text (CSV/TSV) into upload items [{code, phoneNumber, registrationPlate}]
  const parseUploadText = (text) => {
    const lines = text.split(/[\r\n]+/).map(l => l.trim()).filter(l => l);
    const items = [];
    for (const line of lines) {
      const delimiter = line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',';
      const parts = line.split(delimiter).map(p => p.trim().replace(/^["']|["']$/g, ''));
      const code = parts[0];
      if (!code) continue;
      if (looksLikeHeader(code)) continue;
      const rawPhone = parts[1] || '';
      const phone = looksLikeHeader(rawPhone) ? null : normalizePhone(rawPhone);
      const rawPlate = parts[2] || '';
      const plate = (rawPlate && !looksLikeHeader(rawPlate)) ? rawPlate.toUpperCase().trim() : null;
      items.push({ code, phoneNumber: phone, registrationPlate: plate || null });
    }
    return items;
  };

  const initRawRows = (rows) => {
    const nonEmpty = rows.filter(r => r.some(c => c));
    if (nonEmpty.length === 0) return;
    const maxCols = Math.max(...nonEmpty.map(r => r.length));
    // Auto-detect header row
    const autoHeader = looksLikeHeader(nonEmpty[0][0]);
    setHasHeader(autoHeader);
    setCodeColumnIndex(0);
    // Auto-detect phone column: first column where >50% of data rows look like phones
    let autoPhone = null;
    const dataRows = autoHeader ? nonEmpty.slice(1) : nonEmpty;
    for (let ci = 1; ci < maxCols; ci++) {
      const vals = dataRows.map(r => r[ci] || '').filter(v => v);
      if (vals.length > 0 && vals.filter(looksLikePhone).length / vals.length > 0.5) {
        autoPhone = ci;
        break;
      }
    }
    setPhoneColumnIndex(autoPhone);
    // Auto-detect reg plate column: first column (after code) where >50% of data rows look like plates
    let autoRegPlate = null;
    for (let ci = 1; ci < maxCols; ci++) {
      if (ci === autoPhone) continue;
      const vals = dataRows.map(r => r[ci] || '').filter(v => v);
      if (vals.length > 0 && vals.filter(looksLikeRegPlate).length / vals.length > 0.5) {
        autoRegPlate = ci;
        break;
      }
    }
    setRegPlateColumnIndex(autoRegPlate);
    setRawRows(nonEmpty);
    setCsvText('');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isXlsx = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isXlsx) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
          initRawRows(rows.map(r => r.map(c => String(c).trim())));
        } catch (err) {
          console.error('XLSX parse failed:', err);
          showToast(t('generic.errorTitle'), t('contractCodesPage.xlsxParseError'), 'error');
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const lines = e.target.result.split(/[\r\n]+/).filter(l => l.trim());
        const rows = lines.map(line => {
          const delimiter = line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',';
          return line.split(delimiter).map(p => p.trim().replace(/^["']|["']$/g, ''));
        });
        initRawRows(rows);
      };
      reader.readAsText(file);
    }
  };

  // Compute final items from either file mapping or textarea
  const parsedItems = useMemo(() => {
    if (rawRows) {
      const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
      return dataRows
        .filter(row => row[codeColumnIndex])
        .map(row => ({
          code: row[codeColumnIndex],
          phoneNumber: phoneColumnIndex !== null ? normalizePhone(row[phoneColumnIndex] || '') : null,
          registrationPlate: regPlateColumnIndex !== null ? (row[regPlateColumnIndex] || '').toUpperCase().trim() || null : null,
        }));
    }
    if (csvText.trim()) return parseUploadText(csvText);
    return [];
  }, [rawRows, hasHeader, codeColumnIndex, phoneColumnIndex, regPlateColumnIndex, csvText]);

  // 2026-04-30 Anton-review backfill: hämta backend-preview när parsedItems ändras.
  // Visar per-rad-resultat baserat på systemdata INNAN skarp upload.
  // 300ms debounce så vi inte kör request på varje keystroke i textarea.
  useEffect(() => {
    if (parsedItems.length === 0) {
      setPreviewResult(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    const handle = setTimeout(async () => {
      try {
        const token = await getToken();
        const result = await previewContractCodes(token, parkingAreaExtId, parsedItems);
        if (!cancelled) setPreviewResult(result);
      } catch (e) {
        if (!cancelled) {
          console.error('preview fetch failed', e);
          setPreviewError(t('contractCodesPage.previewError'));
          setPreviewResult(null);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(parsedItems), parkingAreaExtId]);

  const handleUploadCodes = async () => {
    if (parsedItems.length === 0) {
      showToast(t('generic.errorTitle'), t('contractCodesPage.noCodesFound'), 'error');
      return;
    }

    const items = parsedItems;
    // B.2: Validate phone count vs available permits
    const phoneCount = items.filter(i => i.phoneNumber).length;
    if (phoneCount > statistics.availablePermits) {
      showToast(t('generic.errorTitle'),
        t('contractCodesPage.notEnoughPermits', { phoneCount, available: statistics.availablePermits }),
        'error');
      return;
    }

    setIsUploading(true);
    try {
      const token = await getToken();
      const result = await uploadContractCodes(token, parkingAreaExtId, items);

      if (result && !result.error) {
        let message = t('contractCodesPage.uploadSuccess', { added: result.codesAdded, skipped: result.duplicatesSkipped });
        if (result.permitsIssued > 0) {
          message += ' ' + t('contractCodesPage.issueSuccessCount', { count: result.permitsIssued });
        }
        showToast(t('generic.successTitle'), message, 'success');
        setUploadResult(result);
        setUploadStep(4);
        loadData();
      }
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleResetCode = async (contractCodeId) => {
    if (!window.confirm(t('contractCodesPage.resetConfirm'))) return;
    try {
      const token = await getToken();
      await resetContractCode(token, contractCodeId);
      showToast(t('generic.successTitle'), t('contractCodesPage.codeReset'), 'success');
      loadData();
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    }
  };

  const handleRevokeCode = async (contractCodeId) => {
    try {
      const token = await getToken();
      await revokeContractCode(token, contractCodeId);
      showToast(t('generic.successTitle'), t('contractCodesPage.codeRevoked'), 'success');
      loadData();
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    }
  };

  const handleReactivateCode = async (contractCodeId) => {
    try {
      const token = await getToken();
      await reactivateContractCode(token, contractCodeId);
      showToast(t('generic.successTitle'), t('contractCodesPage.codeReactivated'), 'success');
      loadData();
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    }
  };

  const handleDeleteCode = async (contractCodeId) => {
    if (!window.confirm(t('contractCodesPage.deleteConfirm'))) return;
    try {
      const token = await getToken();
      await deleteContractCode(token, contractCodeId);
      showToast(t('generic.successTitle'), t('contractCodesPage.codeDeleted'), 'success');
      loadData();
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    }
  };

  const handleOpenIssueModal = async (code) => {
    if (statistics.availablePermits === 0) {
      showToast(t('generic.errorTitle'), t('contractCodesPage.noAvailablePermits'), 'error');
      return;
    }
    setSelectedCodeForIssue(code);
    setIssuePhoneNumber("");
    setIssuePhoneCountryCode("");
    setIssueRegPlate("");
    setSelectedPermitExtId("");
    setAvailablePermitsList([]);
    setIssueUserNotRegistered(false);
    setShowIssueModal(true);

    setPermitSearchQuery("");
    setPermitTotalCount(0);
    try {
      const token = await getToken();
      // Anton-review C3: paginerad endpoint, tar 100 platser i taget.
      // Sökfält i modalen filtrerar via API. Total count visas så user vet om hen
      // behöver söka för att hitta sin plats (vid 400+ platser).
      setPermitsLoading(true);
      const paged = await getAvailablePermitsForIssuePaged(token, parkingAreaExtId, 1, 100)
        .catch(() => null);
      const list = paged?.items || (await getAvailablePermitsForIssue(token, parkingAreaExtId)) || [];
      setAvailablePermitsList(list);
      setPermitTotalCount(paged?.totalCount ?? list.length);
      if (list.length === 1) {
        setSelectedPermitExtId(list[0].permitExtId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPermitsLoading(false);
    }
  };

  // Anton-review C3: debouncad sök mot paginerad endpoint för 400+ platser.
  useEffect(() => {
    if (!showIssueModal || !selectedCodeForIssue) return;
    const handle = setTimeout(async () => {
      try {
        setPermitsLoading(true);
        const token = await getToken();
        const paged = await getAvailablePermitsForIssuePaged(
          token, parkingAreaExtId, 1, 100, permitSearchQuery
        ).catch(() => null);
        if (paged?.items) {
          setAvailablePermitsList(paged.items);
          setPermitTotalCount(paged.totalCount ?? paged.items.length);
        }
      } finally {
        setPermitsLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitSearchQuery]);

  const handleIssuePermit = async () => {
    if (!issuePhoneNumber) {
      showToast(t('generic.errorTitle'), t('contractCodesPage.phoneRequired'), 'error');
      return;
    }

    if (!selectedPermitExtId && availablePermitsList.length > 0) {
      showToast(t('generic.errorTitle'), t('contractCodesPage.noPermitSelected'), 'error');
      return;
    }

    const fullPhoneNumber = issuePhoneCountryCode + issuePhoneNumber;
    setIsIssuing(true);

    try {
      const token = await getToken();
      const result = await issuePermitWithCode(
        token,
        selectedCodeForIssue.contractCodeId,
        fullPhoneNumber,
        issueRegPlate || undefined,
        selectedPermitExtId || undefined
      );

      if (result.success) {
        showToast(
          t('generic.successTitle'),
          t('contractCodesPage.issueSuccess', { userName: result.userName || fullPhoneNumber }),
          'success'
        );
        setShowIssueModal(false);
        loadData();
      } else {
        showToast(t('generic.errorTitle'), result.errorMessage || t('generic.errorDescription'), 'error');
      }
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    } finally {
      setIsIssuing(false);
    }
  };

  const handleOpenScheduleModal = async (code, editing = false) => {
    setSelectedCodeForSchedule(code);
    setIsEditingSchedule(editing);
    setScheduleVehicles([]);
    setSelectedVehicleId("");
    setShowAddVehicle(false);
    setNewRegPlate("");
    if (editing && code.hasQueuedTransition) {
      setSchedulePhoneNumber("");
      setSchedulePhoneCountryCode("");
      setScheduleActiveDate(code.queuedActiveDate ? code.queuedActiveDate.split('T')[0] : "");
      setSchedulePhoneLookupDone(true);
      setShowScheduleModal(true);
      // Auto-lookup vehicles for queued user
      if (code.queuedUserPhone) {
        try {
          const token = await getToken();
          const vehicles = await getVehiclesForPhone(token, code.queuedUserPhone);
          setScheduleVehicles(vehicles || []);
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      setSchedulePhoneNumber("");
      setSchedulePhoneCountryCode("");
      setScheduleActiveDate("");
      setSchedulePhoneLookupDone(false);
      setShowScheduleModal(true);
    }
  };

  const handleLookupVehicles = async () => {
    const fullPhone = schedulePhoneCountryCode + schedulePhoneNumber;
    if (!fullPhone || fullPhone.length < 8) return;

    try {
      const token = await getToken();
      const vehicles = await getVehiclesForPhone(token, fullPhone);
      setScheduleVehicles(vehicles || []);
      setSchedulePhoneLookupDone(true);
      if (vehicles && vehicles.length > 0) {
        setSelectedVehicleId(vehicles[0].vehicleId);
      }
    } catch (error) {
      console.error(error);
      setScheduleVehicles([]);
      setSchedulePhoneLookupDone(true);
    }
  };

  const handleCreateVehicle = async () => {
    if (!newRegPlate.trim()) return;
    // Determine the phone number: use queued user phone for edits, otherwise combine country code + number
    let fullPhone;
    if (isEditingSchedule && selectedCodeForSchedule?.queuedUserPhone) {
      fullPhone = selectedCodeForSchedule.queuedUserPhone;
    } else if (schedulePhoneCountryCode && schedulePhoneNumber) {
      fullPhone = schedulePhoneCountryCode + schedulePhoneNumber;
    } else {
      showToast(t('generic.errorTitle'), t('contractCodesPage.phoneRequired'), 'error');
      return;
    }
    setIsCreatingVehicle(true);
    try {
      const token = await getToken();
      const vehicle = await createVehicleForPhone(token, fullPhone, newRegPlate.trim().toUpperCase());
      if (vehicle) {
        setScheduleVehicles(prev => [...prev, vehicle]);
        setSelectedVehicleId(vehicle.vehicleId);
        setShowAddVehicle(false);
        setNewRegPlate("");
        showToast(t('generic.successTitle'), t('contractCodesPage.vehicleCreated', { plate: vehicle.registrationIdentifier }), 'success');
      }
    } catch (error) {
      console.error(error);
      const errorMsg = error?.status === 400
        ? t('contractCodesPage.vehicleCreateError')
        : t('generic.errorDescription');
      showToast(t('generic.errorTitle'), errorMsg, 'error');
    } finally {
      setIsCreatingVehicle(false);
    }
  };

  const handleScheduleTransition = async () => {
    const isEditing = isEditingSchedule && selectedCodeForSchedule?.hasQueuedTransition;
    const fullPhoneNumber = isEditing
      ? selectedCodeForSchedule.queuedUserPhone
      : schedulePhoneCountryCode + schedulePhoneNumber;

    if (!isEditing && !schedulePhoneNumber) {
      showToast(t('generic.errorTitle'), t('contractCodesPage.phoneRequired'), 'error');
      return;
    }
    if (!scheduleActiveDate) {
      showToast(t('generic.errorTitle'), t('contractCodesPage.activeDateRequired'), 'error');
      return;
    }

    setIsScheduling(true);

    try {
      const token = await getToken();

      // If editing, cancel existing transition first
      if (isEditing) {
        await cancelTransition(token, selectedCodeForSchedule.contractCodeId);
      }

      const result = await scheduleTransition(
        token,
        selectedCodeForSchedule.contractCodeId,
        fullPhoneNumber,
        scheduleActiveDate,
        selectedVehicleId || undefined
      );

      if (result.success) {
        showToast(
          t('generic.successTitle'),
          t('contractCodesPage.scheduleSuccess', { userName: result.userName || fullPhoneNumber, date: scheduleActiveDate }),
          'success'
        );
        setShowScheduleModal(false);
        loadData();
      } else {
        showToast(t('generic.errorTitle'), result.errorMessage || t('generic.errorDescription'), 'error');
      }
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleCancelTransition = async (contractCodeId) => {
    if (!window.confirm(t('contractCodesPage.cancelTransitionConfirm'))) return;
    try {
      const token = await getToken();
      await cancelTransition(token, contractCodeId);
      showToast(t('generic.successTitle'), t('contractCodesPage.transitionCancelled'), 'success');
      loadData();
    } catch (error) {
      console.error(error);
      showToast(t('generic.errorTitle'), t('generic.errorDescription'), 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return d.toISOString().split('T')[0];
  };

  const getStatusBadge = (code) => {
    if (!code.isActive) return <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">{t('contractCodesPage.inactive')}</span>;
    if (code.isUsed) return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">{t('contractCodesPage.used')}</span>;
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">{t('contractCodesPage.available')}</span>;
  };

  return (
    <>
      <Widget>
        <div className="flex flex-col">
          <h2 className="text-xl font-medium mb-1">{t('contractCodesPage.title')}</h2>
          <span>{t('contractCodesPage.description')}</span>
        </div>
      </Widget>

      <Widget className="mt-6">
        <Tabs
          tabs={[
            { title: t('contractCodesPage.tabOverview') || 'Översikt', value: 'overview' },
            { title: t('contractCodesPage.tabUpload') || 'Ladda upp koder', value: 'upload' },
          ]}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </Widget>

      {isLoading && <div className="mt-6"><WidgetLoadingSkeleton /></div>}

      {!isLoading && activeTab === 'overview' && (() => {
        // Klickbara filter-kort: klick på kortet filtrerar koder-tabellen nedanför.
        // Aktivt filter visas med airpark-green ring + bakgrund.
        const FilterCard = ({ label, value, filterKey, valueColor }) => {
          const isActive = statusFilter === filterKey;
          return (
            <button
              type="button"
              onClick={() => setStatusFilter(filterKey)}
              data-testid={`contractcodes-filter-${filterKey}`}
              className={`flex flex-col p-6 shadow rounded-3xl md:w-1/6 transition-all text-left bg-white ${isActive ? 'ring-2 ring-airpark-green bg-airpark-green-background' : 'hover:shadow-md'}`}
            >
              <span className="text-center text-base">{label}</span>
              <span className={`text-center text-xl font-medium mt-3 ${valueColor || ''}`}>{value}</span>
            </button>
          );
        };

        return (
          <div className="flex flex-col md:flex-row mt-6 gap-6">
            <FilterCard
              label={t('contractCodesPage.totalCodes')}
              value={statistics.total}
              filterKey="all"
            />
            <FilterCard
              label={t('contractCodesPage.availableCodes')}
              value={statistics.available}
              filterKey="available"
            />
            <FilterCard
              label={t('contractCodesPage.usedCodes')}
              value={statistics.used}
              filterKey="used"
            />
            <FilterCard
              label={t('contractCodesPage.scheduledTransitions')}
              value={statistics.scheduledTransitions}
              filterKey="scheduled"
              valueColor={statistics.scheduledTransitions > 0 ? 'text-blue-600' : ''}
            />
            <FilterCard
              label={t('contractCodesPage.inactiveCodes')}
              value={statistics.inactive}
              filterKey="inactive"
              valueColor={statistics.inactive > 0 ? 'text-orange-600' : ''}
            />
            {/* Info-kort — inte klickbart (handlar om tillstånd, inte koder) */}
            <Widget className="md:w-1/6">
              <h2 className="text-center">{t('contractCodesPage.availablePermits')}</h2>
              <span className={`text-center text-xl font-medium mt-3 ${statistics.availablePermits === 0 ? 'text-red-600' : ''}`}>
                {statistics.availablePermits}
              </span>
            </Widget>
          </div>
        );
      })()}

      {!isLoading && activeTab === 'upload' && (() => {
        const UPLOAD_STEPS = [
          { title: t('contractCodesPage.uploadStep1Title') },
          { title: t('contractCodesPage.uploadStep2Title') },
          { title: t('contractCodesPage.uploadStep3Title') },
          { title: t('contractCodesPage.uploadStep4Title') },
          { title: t('contractCodesPage.uploadStep5Title') },
        ];

        // För manuell text saknas rawRows → mappnings-steget (idx 2) hoppas över.
        const skipMappingStep = !rawRows;

        const handleStepClick = (idx) => {
          if (idx >= uploadStep) return;
          if (idx === 2 && skipMappingStep) return;
          setUploadStep(idx);
        };

        const handleNextFromFile = () => {
          if (rawRows) setUploadStep(2);
          else if (csvText.trim()) setUploadStep(3);
        };

        const handleResetUpload = () => {
          setRawRows(null);
          setCsvText('');
          setShowManualInput(false);
          setUploadResult(null);
          setPreviewResult(null);
          setPreviewError(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          setUploadStep(0);
        };

        const handleDownloadMall = () => {
          // Tomt upload-blad — bara rubriker. Tidigare hade vi 3 exempel-rader på upload-bladet
          // men det är en risk: glöms de tas bort så skapas A101/A102/A103 som riktiga koder.
          // Exemplen lever vidare i Beskrivnings-bladets Exempel-kolumn istället.
          const ws = XLSX.utils.json_to_sheet([], { header: ['Kod', 'Mobilnummer', 'Regnummer'] });
          ws['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 12 }];
          // Pre-fyll empty text-formaterade celler i 500 rader. Annars coercar Excel
          // talliknande strängar till nummer (t.ex. "+46701234567" → 46701234567 utan +,
          // koder "1213" → tal istället för text). Pre-fyllda celler bär `z:'@'` (text-format)
          // så när användaren skriver i dem hålls text-formatet.
          const TEMPLATE_ROW_COUNT = 500;
          for (let r = 1; r <= TEMPLATE_ROW_COUNT; r++) {
            for (let c = 0; c < 3; c++) {
              const addr = XLSX.utils.encode_cell({ r, c });
              ws[addr] = { t: 's', v: '', z: '@' };
            }
          }
          ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: TEMPLATE_ROW_COUNT, c: 2 } });
          ws['!dataValidation'] = [
            { sqref: 'A2:A1000', type: 'textLength', operator: 'greaterThan', formula1: '0', errorTitle: 'Kod saknas', error: 'Kod är obligatorisk och får inte vara tom.' },
            { sqref: 'B2:B1000', type: 'custom', formula1: 'OR(LEN(B2)=0, AND(LEFT(B2,1)="+", LEN(B2)>=10), AND(LEFT(B2,2)="07", LEN(B2)=10))', errorTitle: 'Ogiltigt mobilnummer', error: 'Mobilnummer ska vara i format +46701234567 eller 0701234567.' },
          ];
          const describe = [
            { Kolumn: 'Kod', Status: 'Obligatorisk', Beskrivning: 'Unik kod per parkeringsområde. Får inte vara tom.', Exempel: 'A101' },
            { Kolumn: 'Mobilnummer', Status: 'Valfri', Beskrivning: 'Format: +46701234567 eller 0701234567. Om ifylld → tillstånd aktiveras automatiskt på användaren med numret (SMS/push skickas). Om tom → öppen kod som kunden själv matar in i appen.', Exempel: '+46701234567' },
            { Kolumn: 'Regnummer', Status: 'Valfri', Beskrivning: 'Förifyller fordonskoppling. Om tom och mobilnummer finns → kunden MÅSTE själv lägga till regnummer i appen (annars risk för parkeringsbot).', Exempel: 'ABC123' },
          ];
          const wsDescribe = XLSX.utils.json_to_sheet(describe);
          wsDescribe['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 80 }, { wch: 18 }];
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Kontraktkoder');
          XLSX.utils.book_append_sheet(wb, wsDescribe, 'Beskrivning');
          XLSX.writeFile(wb, 'kontraktkod_mall.xlsx');
        };

        return (
          <Widget className="mt-6">
            <Stepper
              steps={UPLOAD_STEPS}
              activeStep={uploadStep}
              onStepClick={handleStepClick}
              className="mb-8"
            />

            {/* STEG 1: Förstå format */}
            {uploadStep === 0 && (
              <div className="flex flex-col gap-4" data-testid="contractcodes-upload-step-0">
                <h3 className="text-base font-medium">{t('contractCodesPage.uploadStep1Heading')}</h3>
                <p className="text-sm text-airpark-gray-500">{t('contractCodesPage.uploadFormat')}</p>
                <p className="font-mono text-xs text-airpark-gray-500">{t('contractCodesPage.uploadFormatExample')}</p>
                <ul className="text-xs text-airpark-gray-500 list-disc ml-4 space-y-1">
                  <li>{t('contractCodesPage.helpMobilnumber')}</li>
                  <li>{t('contractCodesPage.helpRegnumber')}</li>
                </ul>
                <div className="flex gap-3 mt-2">
                  <ButtonText isSecondary onClick={handleDownloadMall}>
                    {t('contractCodesPage.downloadTemplate')}
                  </ButtonText>
                  <ButtonText onClick={() => setUploadStep(1)}>
                    {t('contractCodesPage.uploadContinue')}
                  </ButtonText>
                </div>
              </div>
            )}

            {/* STEG 2: Välj fil eller manuell inmatning */}
            {uploadStep === 1 && (
              <div className="flex flex-col gap-4" data-testid="contractcodes-upload-step-1">
                <h3 className="text-base font-medium">{t('contractCodesPage.uploadStep2Heading')}</h3>
                <p className="text-sm text-airpark-gray-500">{t('contractCodesPage.uploadStep2Description')}</p>

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-3 flex-wrap">
                    <ButtonText onClick={() => fileInputRef.current?.click()}>
                      {t('contractCodesPage.chooseFile')}
                    </ButtonText>
                    {rawRows && (
                      <>
                        <span className="text-sm text-airpark-green">
                          ✓ {t('contractCodesPage.fileLoadedRows', { count: rawRows.length })}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setRawRows(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                          className="text-sm text-red-600 hover:underline"
                        >
                          {t('contractCodesPage.removeFile')}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Manuell inmatning — alternativ till fil */}
                {!rawRows && (
                  <>
                    {!showManualInput ? (
                      <button
                        type="button"
                        onClick={() => setShowManualInput(true)}
                        className="text-sm text-airpark-green hover:underline self-start"
                      >
                        {t('contractCodesPage.typeManually')}
                      </button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">{t('contractCodesPage.manualInput')}</span>
                          <button
                            type="button"
                            onClick={() => { setShowManualInput(false); setCsvText(''); }}
                            className="text-sm text-gray-500 hover:underline"
                          >
                            {t('generic.cancel')}
                          </button>
                        </div>
                        <div className="rounded-xl bg-airpark-gray-100 p-3">
                          <div className="text-sm font-medium text-airpark-gray-700 mb-2">
                            {t('contractCodesPage.manualInputFormatHeading')}
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                              <code className="font-mono text-airpark-gray-900 w-60 shrink-0">A101</code>
                              <span className="text-airpark-gray-600">{t('contractCodesPage.manualInputFormatExample1')}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                              <code className="font-mono text-airpark-gray-900 w-60 shrink-0">A101,+46701234567</code>
                              <span className="text-airpark-gray-600">{t('contractCodesPage.manualInputFormatExample2')}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                              <code className="font-mono text-airpark-gray-900 w-60 shrink-0">A101,+46701234567,ABC123</code>
                              <span className="text-airpark-gray-600">{t('contractCodesPage.manualInputFormatExample3')}</span>
                            </div>
                          </div>
                        </div>
                        <textarea
                          className="w-full p-2 border border-airpark-gray-400 rounded-xl h-32 font-mono text-sm focus:outline-none"
                          placeholder={t('contractCodesPage.csvPlaceholder')}
                          value={csvText}
                          onChange={(e) => setCsvText(e.target.value)}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="flex gap-3 mt-4 justify-between flex-wrap">
                  <ButtonText isSecondary onClick={() => setUploadStep(0)}>
                    {t('contractCodesPage.uploadBack')}
                  </ButtonText>
                  <ButtonText
                    onClick={handleNextFromFile}
                    disabled={!rawRows && !csvText.trim()}
                  >
                    {t('contractCodesPage.uploadContinue')}
                  </ButtonText>
                </div>
              </div>
            )}

            {/* STEG 3: Mappa kolumner (endast vid fil-uppladdning) */}
            {uploadStep === 2 && rawRows && (() => {
              const maxCols = Math.max(...rawRows.map(r => r.length));
              const colLabels = Array.from({ length: maxCols }, (_, i) => {
                const header = hasHeader && rawRows[0]?.[i] ? rawRows[0][i] : null;
                return header ? `${header} (kol. ${i + 1})` : `Kolumn ${i + 1}`;
              });
              const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
              const phoneVals = phoneColumnIndex !== null
                ? dataRows.map(r => r[phoneColumnIndex] || '').filter(v => v)
                : [];
              const phoneValidCount = phoneVals.filter(looksLikePhone).length;
              const phoneInvalidCount = phoneVals.length - phoneValidCount;

              return (
                <div className="flex flex-col gap-4" data-testid="contractcodes-upload-step-2">
                  <h3 className="text-base font-medium">{t('contractCodesPage.uploadStep3Heading')}</h3>
                  <p className="text-sm text-airpark-gray-500">{t('contractCodesPage.uploadStep3Description')}</p>

                  <ButtonSlider
                    title={t('contractCodesPage.hasHeader')}
                    isToggled={hasHeader}
                    onClick={() => setHasHeader(h => !h)}
                  />

                  <div className="flex flex-col gap-1">
                    <span className="text-base font-medium pb-1">{t('contractCodesPage.codeColumn')}</span>
                    <select
                      className="rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none"
                      value={codeColumnIndex}
                      onChange={e => setCodeColumnIndex(Number(e.target.value))}
                    >
                      {colLabels.map((label, i) => (
                        <option key={i} value={i}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="text-base font-medium pb-1">{t('contractCodesPage.phoneColumn')}</span>
                    <select
                      className="rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none"
                      value={phoneColumnIndex === null ? '' : phoneColumnIndex}
                      onChange={e => setPhoneColumnIndex(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">{t('contractCodesPage.noPhoneColumn')}</option>
                      {colLabels.map((label, i) => (
                        <option key={i} value={i}>{label}</option>
                      ))}
                    </select>
                    {phoneColumnIndex !== null && phoneVals.length > 0 && (
                      <div className={`mt-1 text-sm px-3 py-2 rounded-lg ${phoneInvalidCount > 0 ? 'bg-orange-50 text-orange-700' : 'bg-green-50 text-green-700'}`}>
                        {phoneInvalidCount > 0
                          ? t('contractCodesPage.phoneValidationWarning', { valid: phoneValidCount, invalid: phoneInvalidCount })
                          : t('contractCodesPage.phoneValidationOk', { count: phoneValidCount })}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="text-base font-medium pb-1">{t('contractCodesPage.regPlateColumn')}</span>
                    <select
                      className="rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none"
                      value={regPlateColumnIndex === null ? '' : regPlateColumnIndex}
                      onChange={e => setRegPlateColumnIndex(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">{t('contractCodesPage.noPhoneColumn')}</option>
                      {colLabels.map((label, i) => (
                        <option key={i} value={i}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-500 mb-2 block">{t('contractCodesPage.previewRows')}</span>
                    <div className="overflow-x-auto border border-airpark-gray-400 rounded-xl">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-airpark-gray-300">
                            {colLabels.map((label, i) => (
                              <th key={i} className={`px-3 py-2 text-left font-medium ${i === codeColumnIndex ? 'text-airpark-green' : i === phoneColumnIndex ? 'text-blue-600' : i === regPlateColumnIndex ? 'text-orange-600' : 'text-gray-400'}`}>
                                {label}
                                {i === codeColumnIndex && ` ✓`}
                                {i === phoneColumnIndex && ` ✓`}
                                {i === regPlateColumnIndex && ` ✓`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dataRows.slice(0, 4).map((row, ri) => (
                            <tr key={ri} className="border-t border-airpark-gray-400">
                              {colLabels.map((_, ci) => (
                                <td key={ci} className={`px-3 py-2 ${ci === codeColumnIndex ? 'font-medium' : ci === phoneColumnIndex ? '' : ci === regPlateColumnIndex ? 'font-mono' : 'text-gray-400'}`}>
                                  {row[ci] || ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {dataRows.length > 4 && (
                      <p className="text-xs text-gray-500 mt-1 pl-1">{t('contractCodesPage.previewRowsMore', { count: dataRows.length - 4 })}</p>
                    )}
                  </div>

                  <div className="flex gap-3 mt-4 justify-between flex-wrap">
                    <ButtonText isSecondary onClick={() => setUploadStep(1)}>
                      {t('contractCodesPage.uploadBack')}
                    </ButtonText>
                    <ButtonText onClick={() => setUploadStep(3)} disabled={parsedItems.length === 0}>
                      {t('contractCodesPage.uploadContinue')}
                    </ButtonText>
                  </div>
                </div>
              );
            })()}

            {/* STEG 4: Förhandsgranska + bekräfta */}
            {uploadStep === 3 && (
              <div className="flex flex-col gap-4" data-testid="contractcodes-upload-step-3">
                <h3 className="text-base font-medium">{t('contractCodesPage.uploadStep4Heading')}</h3>
                <p className="text-sm text-airpark-gray-500">
                  {t('contractCodesPage.uploadStep4Description', { count: parsedItems.length })}
                </p>

                {parsedItems.length > 0 && previewLoading && (
                  <div className="p-3 rounded-lg text-sm border bg-gray-50 border-gray-200 text-gray-600" data-testid="contractcodes-preview-loading">
                    {t('contractCodesPage.previewLoading')}
                  </div>
                )}
                {parsedItems.length > 0 && !previewLoading && previewError && (
                  <div className="p-3 rounded-lg text-sm border bg-red-50 border-red-200 text-red-700" data-testid="contractcodes-preview-error">
                    {previewError}
                  </div>
                )}
                {parsedItems.length > 0 && !previewLoading && previewResult && (() => {
                  const s = previewResult.summary;
                  const exceeds = s.exceedsAvailable;
                  return (
                    <div className="flex flex-col gap-3" data-testid="contractcodes-preview-block">
                      <div className={`p-3 rounded-lg text-sm border flex flex-col gap-1 ${exceeds ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                        <p data-testid="contractcodes-preview-summary">
                          {t('contractCodesPage.previewSummaryHeader', { total: s.totalRows, area: previewResult.parkingAreaName })}
                        </p>
                        <ul className="list-disc list-inside text-xs ml-1">
                          {s.codesToAdd > 0 && (
                            <li>{t('contractCodesPage.previewSummaryAdd', { count: s.codesToAdd })}</li>
                          )}
                          {s.codesToAddAndIssue > 0 && (
                            <li>
                              {t('contractCodesPage.previewSummaryIssue', {
                                count: s.codesToAddAndIssue,
                                existing: s.existingUsersMatched,
                                stub: s.newStubUsersCreated,
                              })}
                            </li>
                          )}
                          {s.newVehiclesCreated > 0 && (
                            <li>{t('contractCodesPage.previewSummaryVehicles', { count: s.newVehiclesCreated })}</li>
                          )}
                          {s.duplicatesInFile > 0 && (
                            <li>{t('contractCodesPage.previewSummaryDupFile', { count: s.duplicatesInFile })}</li>
                          )}
                          {s.duplicatesInDb > 0 && (
                            <li>{t('contractCodesPage.previewSummaryDupDb', { count: s.duplicatesInDb })}</li>
                          )}
                          {s.errorRows > 0 && (
                            <li>{t('contractCodesPage.previewSummaryErrors', { count: s.errorRows })}</li>
                          )}
                        </ul>
                        {exceeds && (
                          <p className="font-medium mt-1">
                            {t('contractCodesPage.notEnoughPermits', {
                              phoneCount: s.codesToAddAndIssue,
                              available: previewResult.availablePermitsWithContractCodeRequirement,
                            })}
                          </p>
                        )}
                      </div>

                      <div className="overflow-x-auto border border-airpark-gray-400 rounded-xl">
                        <table className="w-full text-sm" data-testid="contractcodes-preview-table">
                          <thead>
                            <tr className="bg-airpark-gray-300">
                              <th className="px-3 py-2 text-left font-medium w-12">#</th>
                              <th className="px-3 py-2 text-left font-medium w-40">{t('contractCodesPage.previewColAction')}</th>
                              <th className="px-3 py-2 text-left font-medium">{t('contractCodesPage.previewColCode')}</th>
                              <th className="px-3 py-2 text-left font-medium">{t('contractCodesPage.previewColExplanation')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewResult.rows.map((row) => {
                              const badge =
                                row.action === 'add' ? { label: t('contractCodesPage.actionAdd'), cls: 'bg-blue-100 text-blue-700' } :
                                row.action === 'addAndIssue' ? { label: t('contractCodesPage.actionAddAndIssue'), cls: 'bg-green-100 text-green-700' } :
                                row.action === 'addIssueCreateUser' ? { label: t('contractCodesPage.actionAddIssueCreateUser'), cls: 'bg-emerald-100 text-emerald-700' } :
                                row.action === 'skipDuplicateInFile' ? { label: t('contractCodesPage.actionSkipDupFile'), cls: 'bg-orange-100 text-orange-700' } :
                                row.action === 'skipDuplicateInDb' ? { label: t('contractCodesPage.actionSkipDupDb'), cls: 'bg-gray-200 text-gray-700' } :
                                { label: t('contractCodesPage.actionError'), cls: 'bg-red-100 text-red-700' };
                              return (
                                <tr key={row.rowIndex} className="border-t border-airpark-gray-400 align-top" data-testid={`contractcodes-preview-row-${row.rowIndex}`}>
                                  <td className="px-3 py-2 text-gray-500">{row.rowIndex}</td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`} data-testid={`contractcodes-preview-action-${row.rowIndex}`}>
                                      {badge.label}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 font-mono">{row.code || '—'}</td>
                                  <td className="px-3 py-2" data-testid={`contractcodes-preview-message-${row.rowIndex}`}>
                                    {row.message}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex gap-3 mt-4 justify-between flex-wrap">
                  <ButtonText isSecondary onClick={() => setUploadStep(skipMappingStep ? 1 : 2)}>
                    {t('contractCodesPage.uploadBack')}
                  </ButtonText>
                  <ButtonText
                    onClick={handleUploadCodes}
                    disabled={isUploading || parsedItems.length === 0 || previewLoading || (previewResult && previewResult.summary.exceedsAvailable)}
                  >
                    {isUploading ? t('generic.saving') : t('generic.upload')}
                  </ButtonText>
                </div>
              </div>
            )}

            {/* STEG 5: Klart */}
            {uploadStep === 4 && uploadResult && (
              <div className="flex flex-col gap-4 items-center text-center py-6" data-testid="contractcodes-upload-step-4">
                <div className="w-12 h-12 rounded-full bg-airpark-green-background flex items-center justify-center">
                  <svg className="w-6 h-6 text-airpark-green" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-medium">{t('contractCodesPage.uploadDoneTitle')}</h3>
                <div className="text-sm text-airpark-gray-500 max-w-md">
                  {t('contractCodesPage.uploadSuccess', { added: uploadResult.codesAdded, skipped: uploadResult.duplicatesSkipped })}
                  {uploadResult.permitsIssued > 0 && ' ' + t('contractCodesPage.issueSuccessCount', { count: uploadResult.permitsIssued })}
                </div>
                <ButtonText onClick={handleResetUpload}>
                  {t('contractCodesPage.uploadAnother')}
                </ButtonText>
              </div>
            )}
          </Widget>
        );
      })()}

      {!isLoading && activeTab === 'overview' && (
        <>
          {/* Codes List */}
          <Widget className="mt-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-medium">{t('contractCodesPage.codesList')}</h2>
                {statusFilter !== 'all' && (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-airpark-green-background text-airpark-green text-sm">
                    {t(`contractCodesPage.filterLabel_${statusFilter}`)}
                    <button
                      type="button"
                      onClick={() => setStatusFilter('all')}
                      className="text-airpark-green hover:opacity-70"
                      aria-label={t('contractCodesPage.clearFilter')}
                    >
                      ✕
                    </button>
                  </span>
                )}
              </div>
              <InputSearch
                placeHolder={t('generic.search')}
                value={searchText}
                onChange={(value) => setSearchText(value)}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">{t('contractCodesPage.code')}</th>
                    <th className="text-left p-2">{t('contractCodesPage.permit')}</th>
                    <th className="text-left p-2">{t('generic.status')}</th>
                    <th className="text-left p-2">{t('contractCodesPage.queued')}</th>
                    <th className="text-left p-2">{t('contractCodesPage.usedBy')}</th>
                    <th className="text-left p-2">{t('contractCodesPage.usedAt')}</th>
                    <th className="text-left p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.length === 0 && (
                    <tr><td colSpan="7" className="text-center p-4 text-gray-500">{t('contractCodesPage.noCodes')}</td></tr>
                  )}
                  {filteredCodes.slice(currentPage * 10, (currentPage * 10) + 10).map(code => (
                    <tr key={code.contractCodeId} className={`border-b hover:bg-gray-50 ${!code.isActive ? 'bg-gray-100 opacity-75' : ''} ${code.hasQueuedTransition ? 'border-l-4 border-l-orange-400' : ''}`}>
                      <td className="p-2 font-mono">{code.code}</td>
                      <td className="p-2">{code.permitExtId || "-"}</td>
                      <td className="p-2">{getStatusBadge(code)}</td>
                      <td className="p-2">
                        {code.hasQueuedTransition ? (
                          <span className="text-sm text-orange-700 bg-orange-50 px-2 py-1 rounded" title={`${code.queuedUserPhone || ''}`}>
                            {code.queuedUserName || code.queuedUserPhone || '-'} {code.queuedActiveDate ? formatDate(code.queuedActiveDate) : ''}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="p-2">{code.usedByUserPhone || code.usedByUserName || "-"}</td>
                      <td className="p-2">{formatDate(code.usedAt)}</td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          {!code.isUsed && code.isActive && (
                            <button
                              onClick={() => handleOpenIssueModal(code)}
                              className="text-sm text-green-600 hover:underline"
                              title={statistics.availablePermits === 0 ? t('contractCodesPage.noAvailablePermits') : ''}
                            >
                              {t('contractCodesPage.issue')}
                            </button>
                          )}
                          {code.isUsed && code.isActive && !code.hasQueuedTransition && (
                            <button
                              onClick={() => handleOpenScheduleModal(code)}
                              className="text-sm text-orange-600 hover:underline"
                            >
                              {t('contractCodesPage.scheduleTransition')}
                            </button>
                          )}
                          {code.hasQueuedTransition && (
                            <>
                              <button
                                onClick={() => handleOpenScheduleModal(code, true)}
                                className="text-sm text-blue-600 hover:underline"
                              >
                                {t('contractCodesPage.editTransition')}
                              </button>
                              <button
                                onClick={() => handleCancelTransition(code.contractCodeId)}
                                className="text-sm text-red-600 hover:underline"
                              >
                                {t('contractCodesPage.cancelTransition')}
                              </button>
                            </>
                          )}
                          {code.isUsed && code.isActive && (
                            <button
                              onClick={() => handleResetCode(code.contractCodeId)}
                              className="text-sm text-blue-600 hover:underline"
                            >
                              {t('contractCodesPage.reset')}
                            </button>
                          )}
                          {!code.isUsed && code.isActive && (
                            <button
                              onClick={() => handleRevokeCode(code.contractCodeId)}
                              className="text-sm text-red-600 hover:underline"
                            >
                              {t('contractCodesPage.inactivate')}
                            </button>
                          )}
                          {!code.isActive && (
                            <>
                              <button
                                onClick={() => handleReactivateCode(code.contractCodeId)}
                                className="text-sm text-green-600 hover:underline"
                              >
                                {t('contractCodesPage.reactivate')}
                              </button>
                              <button
                                onClick={() => handleDeleteCode(code.contractCodeId)}
                                className="text-sm text-red-600 hover:underline"
                              >
                                {t('contractCodesPage.delete')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 0 && (
              <Paging
                totalNumberOfPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            )}
          </Widget>
        </>
      )}

      {!isLoading && (
        <>
          {/* Issue Modal */}
          <Modal
            display={showIssueModal}
            setDisplay={setShowIssueModal}
            title={t('contractCodesPage.issueTitle')}
            innerClassName="!w-[760px] !max-w-[95vw] bg-white rounded-lg p-6"
          >
            <div className="flex flex-col gap-4">
              <p className="text-gray-600">
                {t('contractCodesPage.issueDescription', { code: selectedCodeForIssue?.code })}
              </p>

              {statistics.availablePermits === 0 ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{t('contractCodesPage.noAvailablePermits')}</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1">
                    <span className="text-base font-medium pb-1">{t('contractCodesPage.selectPermit')}</span>
                    {/* Anton-review C3: sök för platslista (400+ platser) */}
                    <input
                      type="text"
                      placeholder={t('contractCodesPage.searchPermitPlaceholder')}
                      value={permitSearchQuery}
                      onChange={(e) => setPermitSearchQuery(e.target.value)}
                      className="rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none mb-2"
                    />
                    <div className="text-xs text-gray-500 mb-1">
                      {permitsLoading
                        ? t('contractCodesPage.searchingPermits')
                        : t('contractCodesPage.permitsShownOf', { shown: availablePermitsList.length, total: permitTotalCount })}
                    </div>
                    <div className="border border-airpark-gray-400 rounded-xl max-h-48 overflow-y-auto">
                      {availablePermitsList.map(p => (
                        <label
                          key={p.permitExtId}
                          className={`flex items-center gap-3 px-4 py-2 cursor-pointer border-b border-airpark-gray-400 last:border-b-0 ${selectedPermitExtId === p.permitExtId ? 'bg-airpark-gray-300' : 'hover:bg-airpark-gray-300'}`}
                        >
                          <input
                            type="radio"
                            name="permitSelect"
                            checked={selectedPermitExtId === p.permitExtId}
                            onChange={() => setSelectedPermitExtId(p.permitExtId)}
                            className="rounded"
                          />
                          <span className="text-sm flex flex-wrap gap-x-3 gap-y-0.5">
                            <span><span className="text-gray-500">{t('contractCodesPage.spotIdLabel')}:</span> <span className="font-medium">{p.parkingSpotExtId || p.permitExtId}</span></span>
                            <span><span className="text-gray-500">{t('contractCodesPage.spotNumberLabel')}:</span> <span className="font-medium">{p.spotIdentifier || t('createPermitPage.notNumbered')}</span></span>
                            <span><span className="text-gray-500">{t('contractCodesPage.objectIdLabel')}:</span> <span className="font-medium">{p.objectID || '—'}</span></span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <InputPhoneNumber
                    title={t('contractCodesPage.recipientPhone')}
                    setPhoneNumber={async (number, countryCode) => {
                      setIssuePhoneNumber(number);
                      setIssuePhoneCountryCode(countryCode);
                      setIssueUserNotRegistered(false);
                      if (number && countryCode && number.length >= 5) {
                        try {
                          const token = await getToken();
                          await searchUserByPhoneNumber(token, countryCode + number);
                        } catch (e) {
                          if (e?.status === 404) {
                            setIssueUserNotRegistered(true);
                          }
                        }
                      }
                    }}
                    defaultCountryCode="SE"
                  />

                  {issueUserNotRegistered && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-orange-700 text-sm">{t('contractCodesPage.userNotRegistered')}</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-1">
                    <span className="text-base font-medium pb-1">{t('contractCodesPage.licensePlateOptional')}</span>
                    <input
                      type="text"
                      className="flex grow rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none uppercase"
                      value={issueRegPlate}
                      onChange={(e) => setIssueRegPlate(e.target.value.toUpperCase())}
                      placeholder="ABC123"
                    />
                    {!issueRegPlate && (
                      <p className="text-sm text-blue-600 mt-1">{t('contractCodesPage.regPlateOptionalInfo')}</p>
                    )}
                  </div>

                  <div className="flex gap-2 mt-2">
                    <ButtonText
                      onClick={() => setShowIssueModal(false)}
                      className="flex-1"
                      isSecondary
                    >
                      {t('generic.cancel')}
                    </ButtonText>
                    <ButtonText
                      onClick={handleIssuePermit}
                      disabled={isIssuing || !issuePhoneNumber}
                      className="flex-1"
                    >
                      {isIssuing ? t('generic.saving') : t('contractCodesPage.issue')}
                    </ButtonText>
                  </div>
                </>
              )}
            </div>
          </Modal>

          {/* Schedule Transition Modal */}
          <Modal
            display={showScheduleModal}
            setDisplay={setShowScheduleModal}
            title={isEditingSchedule ? t('contractCodesPage.editTransitionTitle') : t('contractCodesPage.scheduleTransitionTitle')}
            innerClassName="w-[500px]"
          >
            <div className="flex flex-col gap-4">
              <p className="text-gray-600">
                {isEditingSchedule
                  ? t('contractCodesPage.editTransitionDescription', {
                      code: selectedCodeForSchedule?.code,
                      currentQueued: selectedCodeForSchedule?.queuedUserName || selectedCodeForSchedule?.queuedUserPhone || '-'
                    })
                  : t('contractCodesPage.scheduleTransitionDescription', {
                      code: selectedCodeForSchedule?.code,
                      currentUser: selectedCodeForSchedule?.usedByUserName || selectedCodeForSchedule?.usedByUserPhone || '-'
                    })
                }
              </p>

              {isEditingSchedule ? (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="text-sm text-gray-500">{t('contractCodesPage.queuedUser')}</span>
                  <p className="font-medium">{selectedCodeForSchedule?.queuedUserName || selectedCodeForSchedule?.queuedUserPhone || '-'}</p>
                  {selectedCodeForSchedule?.queuedUserPhone && selectedCodeForSchedule?.queuedUserName && (
                    <p className="text-sm text-gray-500">{selectedCodeForSchedule.queuedUserPhone}</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <InputPhoneNumber
                    title={t('contractCodesPage.newTenantPhone')}
                    setPhoneNumber={(number, countryCode) => {
                      setSchedulePhoneNumber(number);
                      setSchedulePhoneCountryCode(countryCode);
                      setSchedulePhoneLookupDone(false);
                      setScheduleVehicles([]);
                      setSelectedVehicleId("");
                    }}
                    defaultCountryCode="SE"
                  />
                  {schedulePhoneNumber && !schedulePhoneLookupDone && (
                    <button
                      type="button"
                      onClick={handleLookupVehicles}
                      className="text-sm text-airpark-green hover:underline self-start mt-1"
                    >
                      {t('contractCodesPage.lookupVehicles')}
                    </button>
                  )}
                </div>
              )}

              {schedulePhoneLookupDone && (
                <div className="flex flex-col gap-2">
                  <span className="text-base font-medium">{t('contractCodesPage.vehicle')}</span>
                  <select
                    className="rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none"
                    value={selectedVehicleId}
                    onChange={(e) => setSelectedVehicleId(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    <option value="">{t('contractCodesPage.noVehicle')}</option>
                    {scheduleVehicles.map(v => (
                      <option key={v.vehicleId} value={v.vehicleId}>{v.registrationIdentifier}</option>
                    ))}
                  </select>

                  {!showAddVehicle ? (
                    <button
                      type="button"
                      onClick={() => setShowAddVehicle(true)}
                      className="text-sm text-airpark-green hover:underline self-start"
                    >
                      {t('contractCodesPage.addNewVehicle')}
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">{t('contractCodesPage.licensePlate')}</span>
                        <input
                          type="text"
                          className="rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none uppercase"
                          value={newRegPlate}
                          onChange={(e) => setNewRegPlate(e.target.value.toUpperCase())}
                          placeholder="ABC123"
                        />
                      </div>
                      <div className="flex gap-2">
                        <ButtonText
                          onClick={handleCreateVehicle}
                          disabled={isCreatingVehicle || !newRegPlate.trim()}
                          className="flex-1"
                        >
                          {isCreatingVehicle ? '...' : t('contractCodesPage.add')}
                        </ButtonText>
                        <ButtonText
                          onClick={() => { setShowAddVehicle(false); setNewRegPlate(""); }}
                          className="flex-1"
                          isSecondary
                        >
                          {t('generic.cancel')}
                        </ButtonText>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-1">
                <span className="text-base font-medium pb-1">{t('contractCodesPage.activeDate')}</span>
                <input
                  type="date"
                  className="flex grow rounded-xl p-2 pl-4 h-11 bg-airpark-gray-300 focus:outline-none"
                  value={scheduleActiveDate}
                  onChange={(e) => setScheduleActiveDate(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                />
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                {t('contractCodesPage.scheduleTransitionInfo')}
              </div>

              <div className="flex gap-2 mt-2">
                <ButtonText
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1"
                  isSecondary
                >
                  {t('generic.cancel')}
                </ButtonText>
                <ButtonText
                  onClick={handleScheduleTransition}
                  disabled={isScheduling || !schedulePhoneNumber || !scheduleActiveDate}
                  className="flex-1"
                >
                  {isScheduling ? t('generic.saving') : (isEditingSchedule ? t('contractCodesPage.saveChanges') : t('contractCodesPage.schedule'))}
                </ButtonText>
              </div>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default ContractCodesPage;

